import { cn } from "~/utils/fontsConfig";
import Image from "next/image";
import { env } from "~/env.mjs";
import { Avatar, AvatarImage } from "~/components/v3/shadcn/Avatar";
import { TEMPORARY_BUCKET_KEY } from "~/utils/constants";
import { CustomizableLayout } from "~/types/CustomizableLayout";

export const CommunityAvatar = ({
    community,
    className,
    height = 40,
    width = 40,
    onCreateImage,
    customization,
}: {
    community?: {
        id: string;
        name?: string | null;
        image?: string | null;
    };
    className?: string;
    height?: number;
    width?: number;
    onCreateImage?: string;
    customization?: CustomizableLayout;
}) => {
    const initial =
        community && community?.name
            ? community.name
                  ?.split(" ")
                  .slice(0, 1)
                  .map((word) => word.charAt(0).toUpperCase())
                  .join("")
            : null;

    const getBgColorAndChar = (initial: string) => {
        if (!initial) return { image: "", textClass: "" };

        const char = initial.toUpperCase();
        const charCode = char.charCodeAt(0);

        const defaultPfps = Array.from(
            { length: 8 },
            (_, i) => `profile_${i + 1}.svg`,
        );

        const index = charCode - 65;
        return defaultPfps[index % defaultPfps.length]!;
    };

    const defaultImage = initial ? getBgColorAndChar(initial) : "profile_1.svg";

    // If the community is not created yet, the image upload path is TEMPORARY_BUCKET_KEY
    if (onCreateImage) {
        return (
            <Avatar
                className={cn(
                    "border-primary transition-[border-color] duration-300 group-hover:border-secondary",
                    className,
                    customization?.className,
                )}
            >
                <AvatarImage
                    src={`${env.NEXT_PUBLIC_ASSETS_URL}/${TEMPORARY_BUCKET_KEY}/${onCreateImage}`}
                    alt="Image"
                />
            </Avatar>
        );
    }

    return (
        <>
            {community && community.image ? (
                <Avatar
                    className={cn(
                        "border-primary transition-[border-color] duration-300 group-hover:border-secondary",
                        className,
                        customization?.className,
                    )}
                >
                    <AvatarImage
                        src={`${env.NEXT_PUBLIC_ASSETS_URL}/community/${community?.id}/${community?.image}`}
                        alt="Image"
                    />
                </Avatar>
            ) : (
                <div
                    className={cn(
                        "relative flex h-10 w-10 items-center justify-center rounded-full",
                        className,
                        customization?.className,
                    )}
                >
                    <Image
                        src={`${env.NEXT_PUBLIC_ASSETS_URL}/profile/default/${defaultImage}`}
                        alt="Community avatar"
                        height={height}
                        width={width}
                        className={cn(
                            "absolute rounded-full object-cover",
                            className,
                        )}
                    />
                    <span
                        className={cn("text-md relative mix-blend-difference")}
                    >
                        {initial ?? "U"}
                    </span>
                </div>
            )}
        </>
    );
};
